import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, CheckCircle2 } from "lucide-react";

const Filters = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Filter Types & Care" 
        description="Identifying, cleaning, and maintaining pool filters"
      >
        <section className="space-y-8">
          {/* Filter Types Overview */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Filter Type Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border p-3 text-left font-semibold">Type</th>
                    <th className="border p-3 text-left font-semibold">Filtration</th>
                    <th className="border p-3 text-left font-semibold">Cleaning</th>
                    <th className="border p-3 text-left font-semibold">Common In</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Cartridge</td>
                    <td className="border p-3">10-20 microns</td>
                    <td className="border p-3">Remove & hose off</td>
                    <td className="border p-3">Most residential pools</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border p-3 font-medium text-foreground">DE (Diatomaceous Earth)</td>
                    <td className="border p-3">2-5 microns (finest)</td>
                    <td className="border p-3">Backwash + recharge</td>
                    <td className="border p-3">Older installations, some premium</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium text-foreground">Sand</td>
                    <td className="border p-3">20-40 microns</td>
                    <td className="border p-3">Backwash only</td>
                    <td className="border p-3">Commercial, some residential</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* When to Clean */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">When to Clean Filters</h2>
            <p className="text-muted-foreground mb-4">
              Filter cleaning is based on pressure, not a calendar. Know the baseline PSI for each system.
            </p>
            <div className="bg-background rounded-lg p-4 border">
              <p className="font-medium text-foreground mb-2">The 8-10 PSI Rule</p>
              <p className="text-sm text-muted-foreground">
                When filter pressure rises <strong>8-10 PSI above the clean baseline</strong>, it's time to clean. 
                Don't wait longer — high pressure reduces flow and strains the pump.
              </p>
            </div>
          </div>

          {/* Cartridge Filter Cleaning */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Cartridge Filter Cleaning</h2>
            <p className="text-muted-foreground mb-4">
              Most common filter type in residential pools. No backwash — you remove and clean the cartridge.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Step-by-Step Procedure</h4>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span><strong className="text-foreground">Turn off pump</strong> — never open filter with pump running</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span><strong className="text-foreground">Release pressure</strong> — open air relief valve on top of filter</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span><strong className="text-foreground">Remove lid</strong> — may need to unclamp band or turn locking ring</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span><strong className="text-foreground">Remove cartridge</strong> — rock gently to break suction, lift straight up</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span><strong className="text-foreground">Hose off</strong> — use garden hose at 45° angle, top to bottom, between each pleat</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">6</span>
                  <span><strong className="text-foreground">Inspect</strong> — check for tears, holes, or damaged end caps</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">7</span>
                  <span><strong className="text-foreground">Reinstall</strong> — seat properly, replace lid, secure clamp/ring</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">8</span>
                  <span><strong className="text-foreground">Prime pump</strong> — open air relief, start pump, close relief when water sprays</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">9</span>
                  <span><strong className="text-foreground">Document</strong> — note new baseline PSI in the app</span>
                </li>
              </ol>
            </div>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Never use a pressure washer!</strong> It destroys the filter 
                  media fibers. Garden hose only.
                </p>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> For deep cleaning (annually or after algae), 
                  soak the cartridge overnight in filter cleaner solution. This removes oils and organics that 
                  hosing alone won't get.
                </p>
              </div>
            </div>
          </div>

          {/* DE Filter Cleaning */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">DE Filter Cleaning</h2>
            <p className="text-muted-foreground mb-4">
              DE filters provide the finest filtration but require more maintenance. The diatomite powder 
              coats internal grids and must be recharged after each backwash.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Backwash Procedure</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Turn off pump</li>
                <li>Set multiport valve to "Backwash"</li>
                <li>Attach backwash hose if needed</li>
                <li>Turn on pump, run until sight glass is clear (2-3 minutes)</li>
                <li>Turn off pump</li>
                <li>Set valve to "Rinse" — run 30 seconds</li>
                <li>Return valve to "Filter"</li>
                <li>Add fresh DE through skimmer (see calculation below)</li>
              </ol>
            </div>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-foreground mb-2">DE Recharge Calculation</h4>
              <p className="text-sm text-muted-foreground mb-2">
                After backwash, add DE based on filter size:
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• <strong>24 sq ft filter:</strong> ~3 lbs DE</li>
                <li>• <strong>36 sq ft filter:</strong> ~4.5 lbs DE</li>
                <li>• <strong>48 sq ft filter:</strong> ~6 lbs DE</li>
                <li>• <strong>60 sq ft filter:</strong> ~7.5 lbs DE</li>
                <li>• <strong>72 sq ft filter:</strong> ~9 lbs DE</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                General rule: 1 lb DE per 10 sq ft of filter area
              </p>
            </div>
          </div>

          {/* Sand Filter */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Sand Filter Maintenance</h2>
            <p className="text-muted-foreground mb-4">
              Sand filters are low-maintenance but less common in premium residential. Cleaning is simple 
              backwashing — no media to remove.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Backwash Procedure</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Turn off pump</li>
                <li>Set multiport valve to "Backwash"</li>
                <li>Turn on pump, run 2-3 minutes until sight glass clears</li>
                <li>Turn off pump, set to "Rinse" for 30 seconds</li>
                <li>Return to "Filter" position</li>
              </ol>
            </div>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-foreground mb-2">Sand Replacement Indicators</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Short filter cycles (frequent backwashing needed)</li>
                <li>• Cloudy water that won't clear</li>
                <li>• Sand in pool (damaged lateral)</li>
                <li>• Sand typically lasts 5-7 years</li>
              </ul>
            </div>
          </div>

          {/* Replacement Indicators */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Replace Cartridges</h2>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Torn or frayed pleats</li>
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Cracked or broken end caps</li>
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Flattened pleats that won't hold shape</li>
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Pressure rises quickly after cleaning</li>
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Visible bypass (debris getting through)</li>
                <li><CheckCircle2 className="h-4 w-4 text-destructive inline mr-2" />Age: typically 2-3 years for residential use</li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> When you notice a cartridge is aging, 
                  note it in the app so we can proactively suggest replacement to the client before it fails.
                </p>
              </div>
            </div>
          </div>

          {/* Documentation */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Documentation Requirements</h3>
            <p className="text-muted-foreground mb-4">
              For every filter clean, log in the app:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• PSI before cleaning</li>
              <li>• PSI after cleaning (new baseline)</li>
              <li>• Condition of filter media</li>
              <li>• Before/after photos</li>
              <li>• Any issues noted (worn cartridge, damaged grid, etc.)</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Filters;